/**
 * useMailboxItem — Office.js hook to read the current Outlook email.
 *
 * Reconciled between v1 (mcp/src/outlook-addin/src/hooks/useMailboxItem.ts)
 * and the existing v2 implementation. We keep v2's simple return shape
 * (`EmailContext | null` returned directly) so existing callers — App.tsx
 * and useClientContext.ts — work unchanged. We pull in v1's gaps:
 *
 *   - HTML body in addition to text body.
 *   - sender displayName + email split (was just the email).
 *   - to/cc recipients.
 *   - attachment size + id (was just name + contentType).
 *   - Dev mock when running in a plain browser (no Office context).
 *   - Re-read on ItemChanged so switching emails refreshes context.
 *
 * ItemChanged comes through the pane's ONE subscription
 * (`outlook/itemChanged.ts`), never a handler of our own: Outlook cannot
 * remove one handler, so a per-hook `removeHandlerAsync` unsubscribed every
 * hook in the pane (story 28633).
 */

import { useEffect, useState } from "react";

import { isDevBuild } from "../devBuild";
import { subscribeItemChanged } from "../outlook/itemChanged";
import { readOraInstanceId } from "../outlook/oraInstanceHeader";

export interface EmailAttachment {
  /** Display filename. */
  name: string;
  /** MIME type from Outlook. */
  contentType: string;
  /** Optional size — Outlook returns bytes; we expose as number when known. */
  size?: number;
  /** Outlook attachment id. */
  id?: string;
}

export interface EmailContext {
  emailId: string;
  /** RFC 5322 Message-ID — stable, immutable, mailbox-independent. Used as the
   *  analyze_email retrieve-first cache key (unlike emailId, which is the
   *  mailbox-specific EWS/REST item id). */
  internetMessageId?: string;
  subject: string;
  /** Plain-text body. */
  body: string;
  /** HTML body — populated when Office returns the HTML coercion. */
  bodyHtml?: string;
  /**
   * Sender — we keep v2's contract that this is a single string. Real
   * Outlook fills it with the email address (`from.emailAddress`); the
   * dev mock fills it with a `Display Name <email>` style string. v1
   * downstream parsers handle both.
   */
  sender: string;
  /** Sender display name when available. */
  senderName?: string;
  /** Sender email address (split out of `sender` for convenience). */
  senderEmail?: string;
  /** to + cc, flat email list. */
  recipients?: string[];
  receivedDate: string;
  attachments: EmailAttachment[];
  /**
   * Graph/REST-format message id (EWS id converted via convertToRestId). Lets
   * the substrate read PDF attachment bytes via Graph when the host doesn't
   * surface them client-side (empty item.attachments). Undefined when
   * conversion isn't available — the backend then degrades to body-only.
   */
  restId?: string;
  /**
   * Mailbox owner SMTP (Office.context.mailbox.userProfile.emailAddress). The
   * substrate reads the message from THIS mailbox via Graph, paired with
   * {@link restId}. Undefined outside an Office host.
   */
  mailboxAddress?: string;
  /**
   * Story 26738 — the run this message is a NOTIFICATION about, read from
   * ora's own `X-Ora-Instance-Id` header. Empty for every ordinary email,
   * which is almost all of them. Set only on mail ora itself sent, and it is
   * what lets a pinned pane open the waiting decision when the Servicer
   * simply clicks the alert in their message list.
   */
  oraInstanceId?: string;
}

const DEV_MOCK_EMAIL: EmailContext = {
  emailId: "mock-msg-dev-002",
  internetMessageId: "<mock-internet-msg-dev-002@highstreetpartners.com>",
  mailboxAddress: "dev.user@highstreetpartners.com",
  subject: "Mortgagee Change Request – Policy TRV-HO-2024-88412",
  body: `Hi team,

Please update the mortgagee on the above policy for our client Sarah Mitchell.

New mortgagee:
  Wells Fargo Bank NA
  123 Main Street, Charlotte, NC 28202
  Loan #: WF-20240815-44821
  Loan Type: First Mortgage

This is a change from the existing mortgagee (Citizens Bank).
Effective immediately.

Thanks,
James R. | Account Manager`,
  bodyHtml: `<p>Hi team,</p><p>Please update the mortgagee on the above policy for our client Sarah Mitchell.</p>`,
  sender: "James R. <james.r@highstreetpartners.com>",
  senderName: "James R.",
  senderEmail: "james.r@highstreetpartners.com",
  recipients: ["serviceteam@highstreetpartners.com"],
  receivedDate: new Date().toISOString(),
  attachments: [],
};

function isBrowserWithoutOfficeMailbox(): boolean {
  try {
    return !Office?.context?.mailbox;
  } catch {
    return true;
  }
}

function getAsync<T>(
  source: { getAsync: (cb: (r: Office.AsyncResult<T>) => void) => void },
): Promise<T | null> {
  return new Promise((resolve) => {
    try {
      source.getAsync((r) => {
        if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
        else resolve(null);
      });
    } catch {
      resolve(null);
    }
  });
}

function getBodyAsync(
  item: Office.MessageRead,
  coercion: Office.CoercionType,
): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      item.body.getAsync(coercion, (r) => {
        if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
        else resolve(null);
      });
    } catch {
      resolve(null);
    }
  });
}

/**
 * Read the currently selected email. Returns `null` until the read
 * completes (matches v2's existing API). Re-reads when the user picks a
 * different email in Outlook (v1 gap).
 */
export function useMailboxItem(): EmailContext | null {
  const [emailContext, setEmailContext] = useState<EmailContext | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Every read is numbered and only the newest may write. Switching A → B → C
    // quickly runs three reads at once, and a heavy body on B can resolve after
    // C's: without this the pane settles on B while C is open.
    let latestRead = 0;

    const read = async () => {
      const readId = ++latestRead;
      const superseded = () => cancelled || readId !== latestRead;

      // Dev / browser preview path — return a deterministic mock so the
      // UI is exercisable without sideloading.
      if (typeof window !== "undefined" && isDevBuild() && isBrowserWithoutOfficeMailbox()) {
        // Tiny delay so the empty-state skeleton has a chance to render.
        await new Promise((r) => setTimeout(r, 200));
        if (!superseded()) setEmailContext(DEV_MOCK_EMAIL);
        return;
      }

      const item = Office?.context?.mailbox?.item as Office.MessageRead | undefined;
      if (!item) {
        // No single message is selected (none, several, or not a message).
        // Keeping the PREVIOUS email here reads as "the pane did not notice
        // the switch"; the strip has a no-item state for exactly this.
        if (!superseded()) setEmailContext(null);
        return;
      }

      // Convert the EWS item id to a Graph/REST id NOW (synchronous, off the
      // raw item.itemId which is EWS-format in read mode) so the backend can
      // read attachment bytes via Graph. Best-effort: if convertToRestId is
      // unavailable or throws, restId stays undefined and the backend degrades
      // to body-only. Must use item.itemId, not the getItemIdAsync override
      // below (that may already be REST on some hosts → double-convert).
      let restId: string | undefined;
      try {
        const ewsId = item.itemId;
        const mbox = Office?.context?.mailbox as
          | {
              convertToRestId?: (id: string, ver: Office.MailboxEnums.RestVersion) => string;
            }
          | undefined;
        if (ewsId && mbox?.convertToRestId) {
          restId = mbox.convertToRestId(ewsId, Office.MailboxEnums.RestVersion.v2_0);
        }
      } catch {
        // best-effort — leave restId undefined
      }

      // Mailbox owner SMTP — the substrate reads the message from this mailbox
      // via Graph (paired with restId). Best-effort: undefined outside a host.
      let mailboxAddress: string | undefined;
      try {
        mailboxAddress = Office?.context?.mailbox?.userProfile?.emailAddress || undefined;
      } catch {
        // best-effort — leave mailboxAddress undefined
      }

      const [bodyText, bodyHtml, oraInstanceId] = await Promise.all([
        getBodyAsync(item, Office.CoercionType.Text),
        getBodyAsync(item, Office.CoercionType.Html),
        // Best-effort and already bounded: resolves "" on any host without
        // Mailbox 1.8, and for every message ora did not send.
        readOraInstanceId(item),
      ]);

      // Some hosts ship `getItemIdAsync` instead of a synchronous
      // `itemId` getter — feature-detect and prefer the async version
      // for accuracy on cached sessions.
      const itemAny = item as unknown as {
        getItemIdAsync?: (cb: (r: Office.AsyncResult<string>) => void) => void;
      };
      let emailId = item.itemId ?? "";
      if (typeof itemAny.getItemIdAsync === "function") {
        const asyncId = await getAsync<string>({
          getAsync: itemAny.getItemIdAsync.bind(itemAny),
        });
        if (asyncId) emailId = asyncId;
      }

      // internetMessageId is the RFC 5322 Message-ID — stable across mailboxes
      // and moves (unlike itemId). Cast defensively in case the @types version
      // predates the property. (mailboxAddress is resolved above alongside restId.)
      const internetMessageId =
        (item as unknown as { internetMessageId?: string }).internetMessageId ?? "";

      const senderName = item.from?.displayName ?? "";
      const senderEmail = item.from?.emailAddress ?? "";
      const sender = senderEmail || senderName;

      const attachments: EmailAttachment[] = (item.attachments ?? []).map((a) => ({
        name: a.name,
        contentType: a.contentType ?? "",
        size: typeof a.size === "number" ? a.size : undefined,
        id: a.id,
      }));

      const recipients: string[] = [
        ...(item.to ?? []).map((r) => r.emailAddress).filter(Boolean),
        ...(item.cc ?? []).map((r) => r.emailAddress).filter(Boolean),
      ];

      if (superseded()) return;
      setEmailContext({
        emailId,
        internetMessageId,
        subject: item.subject ?? "",
        body: bodyText ?? "",
        bodyHtml: bodyHtml ?? undefined,
        sender,
        senderName,
        senderEmail,
        recipients,
        receivedDate: item.dateTimeCreated?.toISOString() ?? "",
        attachments,
        restId,
        mailboxAddress,
        oraInstanceId: oraInstanceId || undefined,
      });
    };

    void read();
    const unsubscribe = subscribeItemChanged(() => {
      void read();
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return emailContext;
}
